import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  LockClosedIcon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const DASHBOARD_PATHS = {
  farmer: '/dashboard',
  supplier: '/supplier/dashboard',
  client: '/client/dashboard'
};

const Academy = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);

  const accountType = user?.accountType || 'farmer';
  const dashboardPath = DASHBOARD_PATHS[accountType] || '/dashboard';
  const billingPath = accountType === 'supplier' ? '/supplier/settings' : '/billing';
  const canSubscribe = accountType === 'farmer' || accountType === 'supplier';
  const hasLockedCourses = courses.some((c) => c.isLocked);

  useEffect(() => {
    loadCourses();
  }, [user]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const res = await academyAPI.listCourses();
      setCourses(res.data.courses || []);
    } catch (error) {
      console.error('Load academy courses error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-primary-50 via-white to-white">
      <div className="pointer-events-none absolute -top-24 -left-16 h-72 w-72 rounded-full bg-primary-200 blur-3xl opacity-30" />
      <div className="pointer-events-none absolute top-40 -right-16 h-96 w-96 rounded-full bg-green-200 blur-3xl opacity-30" />

      <header className="relative max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-4 group">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 via-green-500 to-yellow-500 text-white grid place-items-center shadow-lg">
            <AcademicCapIcon className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xl md:text-2xl font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
              Omabra Poultry Academy
            </span>
            <p className="text-sm text-gray-500 hidden sm:block">Learn poultry farming from experts</p>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to={dashboardPath} className="btn-primary text-sm">Back to dashboard</Link>
          ) : (
            <>
              <Link to="/" className="text-sm text-gray-700 hover:text-gray-900">Home</Link>
              <Link to="/login" className="text-sm text-gray-700 hover:text-gray-900">Sign in</Link>
              <Link to="/register" className="btn-primary text-sm">Get Started</Link>
            </>
          )}
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-6 pb-20">
        <section className="pt-6 pb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-800 mb-4">
            <SparklesIcon className="h-4 w-4" />
            First 2 courses free for everyone
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight max-w-3xl">
            Master poultry farming with structured lessons
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl">
            Practical guides on flock health, feeding, biosecurity, and farm economics.
            Start learning today — subscribe to unlock the full library.
          </p>
        </section>

        {loading ? (
          <div className="py-20 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : courses.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white/80 p-12 text-center">
            <BookOpenIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Courses coming soon</h2>
            <p className="mt-2 text-gray-600">New learning content is being prepared. Check back shortly.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, index) => (
              <CourseCard
                key={course.id}
                course={course}
                index={index}
                unlockPath={user ? billingPath : '/register'}
                canSubscribe={!user || canSubscribe}
              />
            ))}
          </div>
        )}

        {hasLockedCourses && (
          <section className="mt-16 rounded-2xl bg-gradient-to-r from-blue-600 via-green-600 to-emerald-600 p-8 md:p-10 text-white shadow-xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold">Unlock every course</h2>
                <p className="mt-2 text-white/90 max-w-xl">
                  {user && !canSubscribe
                    ? 'The full academy library is part of a paid subscription, which is available on farmer and supplier accounts.'
                    : 'Subscribe to Omabra Poultry Assistant for unlimited access to the full academy library, plus AI guidance and farm management tools. Available on farmer and supplier accounts.'}
                </p>
              </div>
              {(!user || canSubscribe) && (
                <Link
                  to={user ? billingPath : '/register'}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-green-700 hover:bg-green-50 transition-colors shrink-0"
                >
                  {user ? 'Upgrade subscription' : 'Subscribe now'}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const CourseCard = ({ course, index, unlockPath, canSubscribe }) => {
  const isLocked = course.isLocked;
  const CardWrapper = isLocked ? 'div' : Link;
  const wrapperProps = isLocked
    ? { className: 'group block' }
    : { to: `/academy/${course.slug}`, className: 'group block' };

  return (
    <CardWrapper {...wrapperProps}>
      <article className={`relative h-full rounded-2xl border bg-white overflow-hidden shadow-sm transition-all duration-300 ${
        isLocked ? 'border-gray-200 opacity-95' : 'border-gray-100 hover:shadow-lg hover:-translate-y-1'
      }`}>
        <div className="relative h-44 bg-gradient-to-br from-primary-100 to-green-100 overflow-hidden">
          {course.thumbnailUrl ? (
            <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <BookOpenIcon className="h-16 w-16 text-primary-300" />
            </div>
          )}
          {course.isFree && (
            <span className="absolute top-3 left-3 rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white shadow">
              Free
            </span>
          )}
          {isLocked && (
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[1px] flex items-center justify-center">
              <div className="rounded-full bg-white/95 p-3 shadow-lg">
                <LockClosedIcon className="h-6 w-6 text-gray-700" />
              </div>
            </div>
          )}
          <span className="absolute top-3 right-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-gray-700">
            Course {index + 1}
          </span>
        </div>

        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-700 transition-colors line-clamp-2">
            {course.title}
          </h3>
          {course.description && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">{course.description}</p>
          )}
          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <BookOpenIcon className="h-4 w-4" />
              {course.lessonCount} lesson{course.lessonCount !== 1 ? 's' : ''}
            </span>
            {course.estimatedMinutes > 0 && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {course.estimatedMinutes} min
              </span>
            )}
          </div>

          {isLocked ? (
            canSubscribe ? (
              <Link
                to={unlockPath}
                onClick={(e) => e.stopPropagation()}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                Subscribe to unlock
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            ) : (
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-gray-500">
                <LockClosedIcon className="h-4 w-4" />
                Farmer and supplier accounts only
              </span>
            )
          ) : (
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
              Start learning
              <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          )}
        </div>
      </article>
    </CardWrapper>
  );
};

export default Academy;
